import { Box, Paper } from '@mui/material'
import { useMemo } from 'react'
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter'
import {
  oneLight,
  zTouch,
} from 'react-syntax-highlighter/dist/esm/styles/prism'
import { useThemeMode } from '../../../theme/hooks'
import CopyToClipboard from '../CopyToClipboard/CopyToClipboard'
import PlaceholderText from '../PlaceholderText'



/**
 * A reusable component for displaying code blocks with optional formatting, error handling, and additional actions.
 *
 * @param {Object} props - The props for the CodeBlock component.
 * @param {React.ReactNode} props.children - The content to be displayed inside the code block.
 * @param {Function} [props.formatter] - An optional function to format the content before rendering.
 * @param {Object} [props.style={}] - Optional inline styles to apply to the code block.
 * @param {string} [props.noDataMessage] - A message to display when there is no data to show.
 * @param {string} [props.errorMessage] - A message to display when an error occurs.
 * @param {boolean} [props.isJSON=false] - Whether the content is JSON, used to determine if the content is empty.
 * @param {React.ReactNode} [props.actionButton=null] - An optional action button to render below the code block.
 *
 * @returns {JSX.Element} The rendered CodeBlock component.
 */
export const CodeBlock = ({
  children,
  formatter,
  style = {},
  noDataMessage,
  errorMessage,
  isJSON = false,
  actionButton = null,
}) => {
  const { mode } = useThemeMode()

  const code = useMemo(() => {
    if (formatter) {
      return formatter(children)
    }
    return children
  }, [formatter, children])

  const isEmpty = useMemo(() => {
    if (!children) return true
    if (isJSON) {
      try {
        return Object.keys(JSON.parse(children)).length === 0
      } catch {
        return true
      }
    }
    return children.length === 0
  }, [children, isJSON])

  const getContent = () => {
    if (errorMessage) {
      return (
        <Box sx={{textAlign: 'center' }}>
          {errorMessage}
        </Box>
      )
    }

    if (isEmpty) {
      return (
        <PlaceholderText
          variant="overline"
          sx={{ fontSize: '2rem'}}
        >
          {noDataMessage || 'No data available'}
        </PlaceholderText>
      )
    }

    if (isJSON) {
      return (
        <SyntaxHighlighter
          language="json"
          style={mode === 'dark' ? zTouch : oneLight}
        >
          {code}
        </SyntaxHighlighter>
      )
    }

    return code
  }

  return (
    <Box sx={{ position: 'relative' }}>
      {!isEmpty && !errorMessage && !noDataMessage && (
        <CopyToClipboard
          sx={{ position: 'absolute', top: 8, right: 8, zIndex: 1 }}
          text={children}
        />
      )}
      <Paper
        variant="outlined"
        sx={(theme) => ({
          p: 4,
          mt: 2,
          bgcolor: theme.palette.mode === 'dark' ? '#424242' : '#f9f9f9',
          position: 'relative',
          overflow: 'auto',
          width: '100%',
        })}
      >
        <code style={{ whiteSpace: 'nowrap', ...style }}>
          {getContent()}
          {actionButton && (
            <Box sx={{ mt: 2, textAlign: 'center' }}>{actionButton}</Box>
          )}
        </code>
      </Paper>
    </Box>
  )
}
